import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ModalService } from './modal.service';

export interface AppVersionManifest {
  buildId: string;
  gitSha?: string | null;
  builtAt?: string;
}

const POLL_INTERVAL_MS = 3 * 60 * 1000; // Check every 3 minutes while visible
const MIN_CHECK_GAP_MS = 30 * 1000; // Throttle visibility/focus checks to 30s
const HIDDEN_IDLE_RELOAD_MS = 90 * 1000; // Silently refresh hidden idle tab after 90s
const CHUNK_RELOAD_COOLDOWN_MS = 15 * 1000; // Prevent reload loops on genuine network drops
const CACHE_BUST_TIMEOUT_MS = 1500; // Max wait for pre-reload cache-busting fetches
const CHUNK_RELOAD_STORAGE_KEY = 'hv_chunk_reload_at';
const AUTO_BUSTED_BUILD_STORAGE_KEY = 'hv_auto_busted_build_id';
const CACHE_BUST_QUERY_PARAM = '_hv_v';
const BUILD_ID_PLACEHOLDER = '__HV_BUILD_ID__';

@Injectable({
  providedIn: 'root',
})
export class AppVersionService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly modalService = inject(ModalService);

  private initialBuildId: string | null = null;
  private lastCheckAt = 0;
  private checkInFlight = false;
  private isReloading = false;

  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private hiddenReloadTimer: ReturnType<typeof setTimeout> | null = null;
  private routerSub: Subscription | null = null;

  readonly updateAvailable = signal(false);
  readonly bannerDismissed = signal(false);
  readonly isUpdating = signal(false);
  readonly latestBuildId = signal<string | null>(null);

  readonly showUpdateBanner = computed(
    () => this.updateAvailable() && !this.bannerDismissed()
  );

  private readonly onVisibilityChange = () => {
    if (typeof document === 'undefined') return;

    if (document.visibilityState === 'visible') {
      this.clearHiddenReloadTimer();
      void this.checkForUpdate();
    } else if (document.visibilityState === 'hidden' && this.updateAvailable()) {
      this.scheduleHiddenIdleReload();
    }
  };

  private readonly onOnline = () => {
    void this.checkForUpdate();
  };

  /**
   * Starts background deployment monitoring and wires seamless route-transition
   * and idle-background-tab upgrades. Safe no-op in non-browser/test environments
   * or local `ng serve` where `/version.json` is absent.
   */
  init(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    if (this.pollTimer || this.routerSub) {
      return;
    }

    // 1. Clean any temporary cache-busting query param from the address bar
    this.stripCacheBustParam();

    // 2. Read buildId stamped directly into index.html by postbuild.js so we know
    //    the true version of the currently loaded HTML document (even if served from cache).
    const embeddedBuildId = this.readEmbeddedBuildId();
    if (embeddedBuildId) {
      this.initialBuildId = embeddedBuildId;
    }

    // 3. Check remote /version.json on boot, then poll periodically
    void this.checkForUpdate(true, true);

    this.pollTimer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        void this.checkForUpdate();
      }
    }, POLL_INTERVAL_MS);

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('online', this.onOnline);

    // 4. Seamless upgrade on next page route navigation when user is not busy
    this.routerSub = this.router.events.subscribe((event) => {
      if (!(event instanceof NavigationStart)) return;
      if (!this.updateAvailable() || this.isReloading) return;
      if (this.isUserBusy()) return;

      const currentPath = window.location.pathname;
      const targetPath = event.url.split('?')[0].split('#')[0];

      // Only intercept top-level path transitions (never interrupt same-page query param updates)
      if (targetPath && targetPath !== currentPath) {
        void this.performSeamlessReload(event.url);
      }
    });
  }

  /**
   * Checks `/version.json` bypassing HTTP cache. First successful response sets
   * the session baseline (if not already stamped in `<meta name="hv-build-id">`);
   * subsequent responses with a new `buildId` flag an update.
   */
  async checkForUpdate(
    force = false,
    isInitialBootCheck = false
  ): Promise<boolean> {
    if (typeof window === 'undefined' || typeof fetch === 'undefined') {
      return false;
    }
    if (this.checkInFlight || this.isReloading) {
      return this.updateAvailable();
    }
    if (this.updateAvailable() && !force) {
      return true;
    }

    const now = Date.now();
    if (!force && now - this.lastCheckAt < MIN_CHECK_GAP_MS) {
      return false;
    }

    this.checkInFlight = true;
    this.lastCheckAt = now;

    try {
      const response = await fetch(`/version.json?t=${now}`, {
        cache: 'no-store',
        headers: {
          Pragma: 'no-cache',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
        },
      });

      if (!response.ok) {
        return false;
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        // Guard against SPA fallback returning index.html when version.json doesn't exist (e.g. ng serve)
        return false;
      }

      const data = (await response.json()) as Partial<AppVersionManifest>;
      const remoteBuildId =
        typeof data?.buildId === 'string' ? data.buildId.trim() : '';

      if (!remoteBuildId) {
        return false;
      }

      this.latestBuildId.set(remoteBuildId);

      // Lazily populate initialBuildId if index.html didn't have a stamped meta tag
      if (!this.initialBuildId) {
        const embedded = this.readEmbeddedBuildId();
        if (embedded) {
          this.initialBuildId = embedded;
        } else {
          this.initialBuildId = remoteBuildId;
          this.clearLastAutoBustedBuild();
          return false;
        }
      }

      if (remoteBuildId !== this.initialBuildId) {
        // If the page just booted with a stale cached index.html (embedded buildId
        // is older than remote version.json) and we haven't auto-busted for this
        // target build yet, seamlessly cache-bust once right away.
        if (
          isInitialBootCheck &&
          !this.isUserBusy() &&
          this.getLastAutoBustedBuild() !== remoteBuildId
        ) {
          this.setLastAutoBustedBuild(remoteBuildId);
          void this.performSeamlessReload();
          return true;
        }

        this.updateAvailable.set(true);
        if (
          typeof document !== 'undefined' &&
          document.visibilityState === 'hidden'
        ) {
          this.scheduleHiddenIdleReload();
        }
        return true;
      }

      this.clearLastAutoBustedBuild();
      return false;
    } catch {
      // Transient offline / network error — ignore silently
      return false;
    } finally {
      this.checkInFlight = false;
    }
  }

  /**
   * Triggered when the user clicks "Update now" on the non-blocking update pill.
   * Busts the HTTP document cache and reloads the current URL in place while
   * preserving Firebase Auth, cookies, localStorage, sessionStorage, and IndexedDB.
   */
  applyUpdateNow(): void {
    const targetBuild = this.latestBuildId();
    if (targetBuild) {
      this.setLastAutoBustedBuild(targetBuild);
    }
    void this.performSeamlessReload();
  }

  /**
   * Hides the subtle banner for the current session; the app will still seamlessly
   * upgrade on the user's next route navigation or background idle window.
   */
  dismissBanner(): void {
    this.bannerDismissed.set(true);
  }

  /**
   * Handles Angular Router lazy-chunk load failures caused by a new deployment
   * replacing old chunk files. Navigates directly to the target URL once, with
   * a cooldown guard in sessionStorage to prevent infinite reload loops.
   */
  handleNavigationError(error: unknown, targetUrl?: string): void {
    if (!this.isChunkLoadError(error)) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const lastReload = Number(
        window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) || '0'
      );
      const now = Date.now();
      if (now - lastReload < CHUNK_RELOAD_COOLDOWN_MS) {
        return;
      }
      window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
    } catch {
      // Ignore storage access errors in private browsing
    }

    void this.performSeamlessReload(targetUrl);
  }

  isChunkLoadError(error: unknown): boolean {
    const msg =
      error instanceof Error
        ? `${error.name} ${error.message}`
        : String(error ?? '');
    return /ChunkLoadError|Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(
      msg
    );
  }

  /**
   * Builds a cache-busting URL for `targetUrl` (or current `window.location`) by
   * injecting `?_hv_v=<buildId>-<timestamp>`. This guarantees a 100% cache miss
   * across both the browser HTTP disk cache and CDN edge caches (Firebase App Hosting /
   * Cloud CDN), while `index.html` and `init()` immediately strip `_hv_v` via
   * `history.replaceState` on boot.
   */
  buildCacheBustedUrl(targetUrl?: string): string {
    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';
    const rawTarget =
      targetUrl && targetUrl.startsWith('/')
        ? targetUrl
        : typeof window !== 'undefined'
          ? `${window.location.pathname}${window.location.search}${window.location.hash}`
          : '/';

    const parsed = new URL(rawTarget, origin);
    const tokenPrefix = this.latestBuildId() || 'reload';
    parsed.searchParams.set(
      CACHE_BUST_QUERY_PARAM,
      `${tokenPrefix}-${Date.now()}`
    );
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  }

  /**
   * Removes the temporary `_hv_v` cache-busting query parameter from the browser
   * address bar without triggering a navigation or reload.
   */
  stripCacheBustParam(): void {
    if (typeof window === 'undefined' || !window.history?.replaceState) {
      return;
    }
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has(CACHE_BUST_QUERY_PARAM)) {
        return;
      }
      url.searchParams.delete(CACHE_BUST_QUERY_PARAM);
      const cleanUrl = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState(window.history.state, '', cleanUrl);
    } catch {
      // Ignore malformed URL errors in non-standard test hosts
    }
  }

  private readEmbeddedBuildId(): string | null {
    if (typeof document === 'undefined') {
      return null;
    }
    const meta = document.querySelector('meta[name="hv-build-id"]');
    const content = meta?.getAttribute('content')?.trim() || '';
    if (!content || content === BUILD_ID_PLACEHOLDER) {
      return null;
    }
    return content;
  }

  private isUserBusy(): boolean {
    if (this.modalService.stack().length > 0) {
      return true;
    }
    return this.hasFocusedEditableElement();
  }

  private hasFocusedEditableElement(): boolean {
    if (typeof document === 'undefined') return false;
    const active = document.activeElement as HTMLElement | null;
    if (!active) return false;

    const tag = active.tagName?.toLowerCase();
    if (tag === 'textarea' || active.isContentEditable) {
      return true;
    }
    if (tag === 'input') {
      const inputType = ((active as HTMLInputElement).type || 'text').toLowerCase();
      // Ignore checkboxes/buttons; protect active text/number/search/email inputs
      return !['button', 'checkbox', 'radio', 'submit', 'reset'].includes(inputType);
    }
    return false;
  }

  private scheduleHiddenIdleReload(): void {
    this.clearHiddenReloadTimer();
    this.hiddenReloadTimer = setTimeout(() => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden' &&
        this.updateAvailable() &&
        !this.isUserBusy()
      ) {
        void this.performSeamlessReload();
      }
    }, HIDDEN_IDLE_RELOAD_MS);
  }

  private clearHiddenReloadTimer(): void {
    if (this.hiddenReloadTimer) {
      clearTimeout(this.hiddenReloadTimer);
      this.hiddenReloadTimer = null;
    }
  }

  private async performSeamlessReload(targetUrl?: string): Promise<void> {
    if (typeof window === 'undefined' || this.isReloading) {
      return;
    }
    this.isReloading = true;
    this.isUpdating.set(true);
    this.clearHiddenReloadTimer();

    await this.bustDocumentAndSwCaches(targetUrl);

    const bustedUrl = this.buildCacheBustedUrl(targetUrl);
    this.navigateToUrl(bustedUrl);
  }

  /**
   * Refreshes the browser's HTTP disk cache entry for the clean SPA path and `/index.html`,
   * clears Service Worker Cache Storage (`window.caches`), and updates Service Worker
   * registrations — without ever touching cookies, localStorage, sessionStorage, or IndexedDB.
   */
  private async bustDocumentAndSwCaches(targetUrl?: string): Promise<void> {
    const cleanPath =
      targetUrl && targetUrl.startsWith('/')
        ? targetUrl.split('?')[0].split('#')[0] || '/'
        : typeof window !== 'undefined'
          ? window.location.pathname || '/'
          : '/';

    const tasks: Promise<unknown>[] = [];

    if (typeof fetch !== 'undefined') {
      const fetchInit: RequestInit = {
        cache: 'reload',
        credentials: 'same-origin',
        headers: {
          Pragma: 'no-cache',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
        },
      };
      tasks.push(fetch(cleanPath, fetchInit).catch(() => undefined));
      if (cleanPath !== '/index.html') {
        tasks.push(fetch('/index.html', fetchInit).catch(() => undefined));
      }
    }

    if (typeof window !== 'undefined' && 'caches' in window && window.caches) {
      tasks.push(
        window.caches
          .keys()
          .then((keys) =>
            Promise.allSettled(keys.map((key) => window.caches.delete(key)))
          )
          .catch(() => undefined)
      );
    }

    if (
      typeof navigator !== 'undefined' &&
      navigator.serviceWorker?.getRegistrations
    ) {
      tasks.push(
        navigator.serviceWorker
          .getRegistrations()
          .then((regs) =>
            Promise.allSettled(regs.map((reg) => reg.update()))
          )
          .catch(() => undefined)
      );
    }

    if (tasks.length === 0) {
      return;
    }

    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    const timeoutPromise = new Promise<void>((resolve) => {
      timeoutHandle = setTimeout(resolve, CACHE_BUST_TIMEOUT_MS);
    });

    try {
      await Promise.race([Promise.allSettled(tasks), timeoutPromise]);
    } finally {
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }
    }
  }

  protected navigateToUrl(url: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.location.replace(url);
    } catch {
      window.location.reload();
    }
  }

  private getLastAutoBustedBuild(): string | null {
    try {
      return window.sessionStorage.getItem(AUTO_BUSTED_BUILD_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private setLastAutoBustedBuild(buildId: string): void {
    try {
      window.sessionStorage.setItem(AUTO_BUSTED_BUILD_STORAGE_KEY, buildId);
    } catch {
      // Ignore storage access errors in private browsing
    }
  }

  private clearLastAutoBustedBuild(): void {
    try {
      window.sessionStorage.removeItem(AUTO_BUSTED_BUILD_STORAGE_KEY);
    } catch {
      // Ignore storage access errors in private browsing
    }
  }

  ngOnDestroy(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.clearHiddenReloadTimer();
    this.routerSub?.unsubscribe();
    this.routerSub = null;

    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onOnline);
    }
  }
}
